import { useEffect, useState } from "react";
import { Icon, divIcon } from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import { osm } from '../mapTiler/osmProvider.js';
import { waterCategories, airCategories, landCategories, snowCategories, bestViewsCategories } from '../data/categoryList.js';
import Loader from "./Loader.jsx";
import userMarker from '../static/images/markers/user_marker.png';
import temporaryMarker from '../static/images/markers/temporary_marker.png';
import { Link } from "react-router-dom";
import Error from '../components/Error.jsx';
import { useTranslation } from "react-i18next";
import useRequireLogin from "../hooks/useRequireLogin";

// Questo componente viene inchiodato dal primo componente <Marker>

// Helper traduzione difficoltà (allineato a ActivityCard.jsx)
const difficultyBadgeBg = (level) => {
    if (level === 'Facile') return '#22c55e';
    if (level === 'Medio') return '#eab308';
    if (level === 'Difficile') return '#ef4444';
    return 'transparent';
};

const AllActivitiesMap = ({ markersData, fillContainer = false, activitiesCount = null }) => {
    const { t } = useTranslation();

    const setDifficultyLevelCopy = (level) => {
        if (level === 'Facile') return t('ActivityCard.easy');
        if (level === 'Medio') return t('ActivityCard.medium');
        if (level === 'Difficile') return t('ActivityCard.hard');
        return level;
    };
    const [zoomLevel] = useState(10);
    const [center] = useState([46.36261666239876, 8.80477693372903]);
    // Gestisco apertura card
    const initialState = {
        id: null,
        lat: null,
        lng: null
    }
    const [modal, setModal] = useState(initialState);
    const handleMarkerClick = (id, lat, lng) => {
        modal === id ? setModal(initialState) : setModal({ id, lat, lng })
    }

    // Rilevo posizione utente
    const [position, setPosition] = useState(null);
    const [isPositionLoading, setIsPositionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isGeolocationChecked, setIsGeolocationCheck] = useState(false);
    useEffect(() => {
        if (error) {
            if (error.code === 1 && !isGeolocationChecked) {
                setIsGeolocationCheck(true);
                setError(prevState => ({
                    code: 1,
                    message: t('MapPage.AllActivitiesMap.geolocationDenied')
                }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error])

    const getPosition = () => {
        setIsPositionLoading(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setPosition({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                    setError(null);
                    setIsPositionLoading(false);
                },
                (error) => {
                    setPosition(null);
                    setError(error);
                    setIsPositionLoading(false);
                }
            );
        } else {
            setError('Geolocalizzazione non supportato dal browser.')
        }
    }

    const userIcon = new Icon({
        iconUrl: userMarker,
        iconSize: [32, 32]
    })

    // Vola alla posizione utente ogni volta che `position` cambia (cioè ad ogni click sul bottone "Rileva posizione").
    // Ogni getPosition con successo crea una nuova reference dell'oggetto → effect rifa flyTo → mappa si ricentra.
    const FlyToUserPosition = () => {
        const map = useMap();
        useEffect(() => {
            if (position) {
                map.flyTo([position.lat, position.lng], 16);
            }
            // position resta in deps: il flyTo deve riscattare a ogni nuova posizione (vedi commento sopra).
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [position, map]);
        return null;
    }

    // Gestisco la selezione dell'icona relativa alla categoria
    const selectCategoryIcon = (categoryLabel) => {
        let allCategories = [
            ...waterCategories,
            ...airCategories,
            ...landCategories,
            ...snowCategories,
            ...bestViewsCategories
        ]
        let category = allCategories.find((element) => element.name === categoryLabel);
        const iconUrl = category?.icon || temporaryMarker;
        return createCustomIcon(categoryLabel, iconUrl);
    }

    // Funzione per generare un'icona customizzata con div
    const createCustomIcon = (categoryLabel, iconUrl) => {
        // Determino il colore del bordo in base alla categoria
        const getBorderColor = () => {
            if (waterCategories.some(cat => cat.name === categoryLabel)) return '#37abc8';
            if (airCategories.some(cat => cat.name === categoryLabel)) return '#d9d9d9';
            if (landCategories.some(cat => cat.name === categoryLabel)) return '#92c730';
            if (snowCategories.some(cat => cat.name === categoryLabel)) return '#b9dfff';
            if (bestViewsCategories.some(cat => cat.name === categoryLabel)) return '#ffd815';
            return '#666666';
        };
        return divIcon({
            className: 'custom-marker',
            html: `
                <div style="
                    width: 48px;
                    height: 48px;
                    border-radius: 30%;
                    border: 3px solid ${getBorderColor()};
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                ">
                    <img 
                        src="${iconUrl}" 
                        style="
                            width: 32px;
                            height: 32px;
                            object-fit: contain;
                        "
                    />
                </div>
                `,
            iconSize: [48, 48],
            iconAnchor: [24, 24],
            popupAnchor: [0, -24],
        })
    }

    // Calcolo il punto centrale fra tutte le attività presenti nella mappa per volare verso il punto medio
    const [averagePoint, setAveragePoint] = useState(null);
    useEffect(() => {
        // Controllo che markersData non sia valido
        if (!markersData || markersData.length === 0) {
            setAveragePoint(null);
            return;
        }
        // Filtro solo i marker con coordinate valide
        const validMarkers = markersData.filter(element =>
            element.lat && element.lng &&
            !isNaN(element.lat) && !isNaN(element.lng)
        )
        // Se non ci sono markers validi, non calcolo il punto medio
        if (validMarkers.length === 0) {
            setAveragePoint(null);
            return;
        }
        // Media delle x
        let totalX = 0
        validMarkers.forEach(element => totalX = totalX + element.lat);
        const xAverage = totalX / validMarkers.length;
        // Media delle y
        let totalY = 0
        validMarkers.forEach(element => totalY = totalY + element.lng);
        const yAverage = totalY / validMarkers.length;
        // Controllo finale che le coordinate calcolate siano valide
        if (!isNaN(xAverage) && !isNaN(yAverage)) {
            setAveragePoint([xAverage, yAverage]);
        } else {
            setAveragePoint(null);
        }
    }, [markersData])

    // Componente che permette di spostarsi nel punto medio dopo che viene calcolato
    const [isAverageFlew, setIsAverageFlew] = useState(false);
    const FlyToAveragePoint = ({ averagePoint }) => {
        const map = useMap();
        useEffect(() => {
            if (averagePoint && !isAverageFlew &&
                !isNaN(averagePoint[0]) && !isNaN(averagePoint[1])) {
                map.flyTo(averagePoint, 11);
                setIsAverageFlew(true)
            }
        }, [averagePoint, map]);
        return null;
    }

    const navigateTo = useRequireLogin();
    const handleDetailsClick = (elementId) => {
        navigateTo(`/activity-details?id=${elementId}`);
    }

    // Mobile fill: il wrapper riempie l'altezza imposta dal genitore (MapPage).
    // Desktop o pagine non-fill: layout originario con altezza fissa.
    // In modalità fillContainer (mobile MapPage immersivo) togliamo border/rounded e teniamo solo overflow:hidden;
    // su desktop manteniamo il look originario.
    const wrapperClass = fillContainer
        ? "relative z-10 w-full h-full rounded-xl lg:w-[800px] lg:h-auto lg:my-8 lg:border-2 overflow-hidden"
        : "relative z-10 w-full lg:w-[800px] lg:my-8 border-2 rounded-xl overflow-hidden";
    const mapClass = fillContainer
        ? "h-full lg:h-[380px] w-full"
        : "h-[calc(100svh-220px)] lg:h-[380px] w-full";

    return (
        <div className={wrapperClass}>
            <MapContainer className={mapClass} center={center} zoom={zoomLevel}>
                <TileLayer url={osm.maptiler.url} attribution={osm.maptiler.attribution} />
                {averagePoint && !isAverageFlew && <FlyToAveragePoint averagePoint={averagePoint} />}
                <FlyToUserPosition />
                {
                    markersData && markersData.length > 0 && markersData.map((element, index) => {
                        // salto se lat o lng non sono numeri validi
                        if (isNaN(element.lat) || isNaN(element.lng)) {
                            console.warn(`Invalid coordinates for element with id ${element.id}`);
                            return null;
                        }
                        return <Marker
                            key={index}
                            position={[element.lat, element.lng]}
                            icon={selectCategoryIcon(element.category)}
                            eventHandlers={{
                                click: () => handleMarkerClick(element.id, element.lat, element.lng)
                            }}
                        >
                            <Popup className="activity-popup" minWidth={200} maxWidth={200}>
                                {/* Foto cover — cliccabile: porta al dettaglio */}
                                <div
                                    style={{ position: 'relative', width: '100%', height: '130px', overflow: 'hidden', cursor: 'pointer' }}
                                    onClick={() => handleDetailsClick(element.id)}
                                >
                                    {element.cover
                                        ? <img src={element.cover.resizedUrl} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} alt={element.title} />
                                        : <div style={{ width: '100%', height: '100%', background: '#e5e5e5' }} />
                                    }
                                    {element.difficultyLevel && element.difficultyLevel.length > 0 && (
                                        <div style={{ position: 'absolute', bottom: '8px', left: '8px', display: 'flex', gap: '4px' }}>
                                            {element.difficultyLevel.map((level, i) => (
                                                <span
                                                    key={i}
                                                    style={{
                                                        backgroundColor: difficultyBadgeBg(level),
                                                        color: 'white',
                                                        fontSize: '11px',
                                                        padding: '2px 6px',
                                                        borderRadius: '6px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {setDifficultyLevelCopy(level)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Info */}
                                <div style={{ width: '100%', padding: '10px 12px 10px' }}>
                                    <div
                                        style={{ fontWeight: '600', fontSize: '13px', color: '#404040', lineHeight: '1.3', marginBottom: '8px', cursor: 'pointer' }}
                                        onClick={() => handleDetailsClick(element.id)}
                                    >
                                        {element.title}
                                    </div>
                                    {element.lat && element.lng && (
                                        <Link to={element.roadLink} target="_blank" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#225424', fontStyle: 'italic' }}>
                                            <i className="fi fi-rr-route" style={{ lineHeight: 0 }}></i>
                                            <span>{t('MapPage.AllActivitiesMap.popupDirections')}</span>
                                        </Link>
                                    )}
                                </div>
                            </Popup>
                        </Marker>
                    })
                }
                {
                    position &&
                    <Marker position={[position.lat, position.lng]} icon={userIcon} />
                }
            </MapContainer>

            {/* Top center: contatore attività (mobile + desktop) + bottone Visualizza elenco (solo desktop) */}
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1000] flex flex-col items-center gap-1">
                {activitiesCount !== null && (
                    <div className="text-xs bg-white/90 text-[#225424] px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                        <span className="font-semibold">{activitiesCount}</span> {t('AllActivities.activitiesFound')}
                    </div>
                )}
                {/* Visualizza elenco — solo desktop (su mobile va in basso a destra, icon-only) */}
                <Link to="/all-activities" className="hidden md:block">
                    <div className="flex gap-2 bg-[#225424] text-white py-2 px-4 rounded-xl cursor-pointer shadow-md whitespace-nowrap">
                        <i className="fi fi-rr-map mt-0.5"></i>
                        <div>{t('MapPage.listButton')}</div>
                    </div>
                </Link>
            </div>

            {/* Bottom center: Rileva posizione — solo desktop */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] hidden md:block">
                {!position && !isPositionLoading && !error && <button className="w-fit flex gap-2 items-center py-2 px-3 bg-[#225424] text-white rounded-full shadow-md whitespace-nowrap" onClick={getPosition}><i className="fi fi-rr-location-crosshairs mt-1"></i><div>{t('MapPage.AllActivitiesMap.checkPosition')}</div></button>}
                {!position && !isPositionLoading && error && <div className="bg-white rounded-xl px-3 py-2 shadow-md text-sm max-w-[260px] text-center"><Error text={error.message} message={error.code === 1 && t('MapPage.AllActivitiesMap.geolocationInstruction')} /></div>}
                {isPositionLoading && <div className="bg-white rounded-full p-2 shadow-md"><Loader /></div>}
            </div>

            {/* Bottom-right: bottoni icon-only impilati verticalmente — solo mobile */}
            <div className="absolute bottom-6 right-2 z-[1000] flex flex-col gap-2 items-end md:hidden">
                <Link to="/all-activities" aria-label={t('MapPage.listButton')} title={t('MapPage.listButton')}>
                    <div className="w-12 h-12 pt-1.5 flex items-center justify-center bg-white text-[#225424] rounded-full shadow-lg cursor-pointer">
                        <i className="fi fi-rr-map text-lg"></i>
                    </div>
                </Link>
                {/* Rileva posizione: sempre visibile. Niente loader; ad ogni click la mappa rifa flyTo. */}
                <button
                    className="w-12 h-12 pt-1.5 flex items-center justify-center bg-white text-[#225424] rounded-full shadow-lg"
                    onClick={getPosition}
                    aria-label={t('MapPage.AllActivitiesMap.checkPosition')}
                    title={t('MapPage.AllActivitiesMap.checkPosition')}
                >
                    <i className="fi fi-rr-location-crosshairs text-lg"></i>
                </button>
                {error && (
                    <div className="bg-white rounded-xl px-3 py-2 shadow-md text-xs max-w-[200px] text-right">
                        <Error text={error.message} message={error.code === 1 && t('MapPage.AllActivitiesMap.geolocationInstruction')} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default AllActivitiesMap;
